<template>
  <div class="d-f fd-c fd-md-r f-g" v-if="done">
    <div class="f-1-5">
      <transactions-list
        forceType="stripe_donation"
        forceStatus="completed"
        title="donations"
        :disableSearch="true"
        :skipTicket="true"
        :hideFilters="true"
        :hideTypes="true"
      />
    </div>
    <div class="f-1">
      <card-minimal title="Enable" class="m-t-2 m-t-md-0">
        <div class="p-a-4">
          <Toggle
            explanation="Make it easy for users to donate to you by activating our built-in donationservice."
            :value="enabled"
            @change="toggleDonations"
          />
        </div>
      </card-minimal>
      <card-minimal title="Preset amounts" subtitle="Set one or more amounts to 0 if you do not want any preset amounts" class="m-t-4">
        <div class="p-x-4 p-b-4 p-t-1">
          <FormInput class="center-text" :stepper="true" type="number" :step="1" :min="0" :value="parseInt(amounts.first, 10)" @input="amounts.first = parseInt($event, 10)" :error="correctAmount(amounts.first) ? '' : 'Too low'" />
          <FormInput class="center-text m-t-1" :stepper="true" type="number" :step="1" :min="0" :value="parseInt(amounts.second, 10)" @input="amounts.second = parseInt($event, 10)" :error="correctAmount(amounts.second) ? '' : 'Too low'" />
          <FormInput class="center-text m-t-1" :stepper="true" type="number" :step="1" :min="0" :value="parseInt(amounts.third, 10)" @input="amounts.third = parseInt($event, 10)" :error="correctAmount(amounts.third) ? '' : 'Too low'" />
          <FormDropdown
            class="m-t-1"
            placeholder="Set currency"
            :items="currencies"
            @input="selectedCurrency = $event.value"
          />
          <Button
            class="m-t-2 f-w"
            :disabled="!canUpdateAmounts"
            :loading="states.amounts === 'updating'"
            @click="updateAmounts"
          >Update amounts</Button>
        </div>
      </card-minimal>
      <card-minimal title="Texts" subtitle="Set your own donations texts" class="m-t-4">
        <div class="p-x-4 p-b-4 p-t-1">
          <div class="d-f f-g fd-r">
            <Button @click="donationTextLanguage = 'en'" :class="`f-1 bold ${donationTextLanguage === 'en' ? 'gray' : 'gray outline'}`">English</Button>
            <Button @click="donationTextLanguage = 'sv'" :class="`f-1 bold ${donationTextLanguage === 'sv' ? 'gray' : 'gray outline'}`">Swedish</Button>
          </div>
          <p class="card-content-title">
            <strong>Donation button</strong>
          </p>
          <FormInput :placeholder="donationTextLanguage === 'en' ? 'Donate' : 'Donera'" type="text" class="f-w" v-model="translations.button[donationTextLanguage]" />
          <p class="card-content-title m-t-2">
            <strong>Message</strong>
          </p>
          <form-textarea class="small" :toolbar="['bold', 'ul', 'link']" height="185" v-model="translations.message[donationTextLanguage]" :key="`m-${donationTextLanguage}`" />
          <p class="card-content-title m-t-2">
            <strong>Thank you message</strong>
          </p>
          <form-textarea class="small" :toolbar="['bold', 'ul', 'link']" height="185" v-model="translations.thanks[donationTextLanguage]" :key="`tym-${donationTextLanguage}`" />
          <Button
            class="m-t-2 f-w"
            :loading="states.texts === 'updating'"
            @click="updateTexts"
          >Update texts</Button>
        </div>
      </card-minimal>
    </div>
  </div>
</template>

<script>

import TransactionsList from '@/components/Commerce/TransactionsList.vue';
import Toggle from '@/components/toggle.vue';

export default {
  components: {
    Toggle,
    TransactionsList,
  },
  computed: {
    /**
     * Return a list of currencies available
     * @returns {Array}
     */
    currencies() {
      return this.$store.getters['AccountManager/allCurrencies'].map(currency => ({
        value: currency,
        string: currency.toUpperCase(),
        selected: currency === this.selectedCurrency.toLowerCase(),
      }));
    },
    /**
     * Return default currency
     * @returns {String}
     */
    defaultCurrency() {
      return this.$store.getters['AccountManager/defaultCurrency'];
    },
    /**
     * Return boolean if the amounts are correct and updateable
     * @returns {Boolean}
     */
    canUpdateAmounts() {
      return this.correctAmount(this.amounts.first) && this.correctAmount(this.amounts.second) && this.correctAmount(this.amounts.third);
    },
  },
  methods: {
    /**
     * Gets and sets donations settings
     * @returns {void}
     */
    async getDonateSettings() {
      const {
        enabled = false,
        currency,
        translations: {
          button,
          message,
          thanks,
        },
        amounts,
      } = await this.$store.dispatch('Donations/getSettings');

      const divideBy = this.currency_divider(currency || this.defaultCurrency);

      this.selectedCurrency = currency;

      this.enabled = enabled;

      this.amounts = {
        first: amounts && amounts[0] ? amounts[0] / divideBy : 0,
        second: amounts && amounts[1] ? amounts[1] / divideBy : 0,
        third: amounts && amounts[2] ? amounts[2] / divideBy : 0,
      };

      this.translations = {
        button: button || { sv: '', en: '' },
        message: message || { sv: '', en: '' },
        thanks: thanks || { sv: '', en: '' },
      };
    },
    /**
     * Update the texts
     * @returns {void}
     */
    async updateTexts() {
      this.states.texts = 'updating';

      const updated = await this.$store.dispatch('Donations/updateSettings', { donation: { translations: this.translations } });

      this.states.texts = '';

      if (!updated) {
        swal({
          title: this.$t('errors.error_occured'),
          text: 'Could not update your translation texts right now. Please try again.',
          icon: 'error',
        });
      }
    },
    /**
     * Toggle donations
     * @returns {void}
     */
    async toggleDonations(bool) {
      const updated = await this.$store.dispatch('Donations/updateSettings', { donation: { enabled: bool } });
      if (!updated) {
        swal({
          title: this.$t('errors.error_occured'),
          text: `Could not ${bool ? 'enable' : 'disable'} donations right now. Please try again.`,
          icon: 'error',
        });
      }
    },
    /**
     * Update the amounts
     * @returns {void}
     */
    async updateAmounts() {
      if (!this.canUpdateAmounts) {
        return;
      }

      this.states.amounts = 'updating';

      const payload = {
        amounts: [],
        currency: this.selectedCurrency,
      };

      payload.amounts[0] = this.amounts.first ? this.amounts.first * this.currency_divider(this.selectedCurrency) : 0;
      payload.amounts[1] = this.amounts.second ? this.amounts.second * this.currency_divider(this.selectedCurrency) : 0;
      payload.amounts[2] = this.amounts.third ? this.amounts.third * this.currency_divider(this.selectedCurrency) : 0;

      const updated = await this.$store.dispatch('Donations/updateSettings', { donation: payload });

      this.states.amounts = '';

      if (!updated) {
        swal({
          title: this.$t('errors.error_occured'),
          text: 'Could not update your amounts right now. Please try again.',
          icon: 'error',
        });
      }
    },
    /**
     * Return the correct divider to use with certain currencies
     * @return {Number}
     */
    currency_divider(currency) {
      switch (currency) {
        case 'bif':
        case 'clp':
        case 'djf':
        case 'gnf':
        case 'jpy':
        case 'kmf':
        case 'krw':
        case 'mga':
        case 'pyg':
        case 'rwf':
        case 'ugx':
        case 'vnd':
        case 'vuv':
        case 'xaf':
        case 'xof':
        case 'xpf':
          return 1;
        default:
          return 100;
      }
    },
    /**
     * Returns an error if amount is not withing desired range
     * @return {(boolean|String)}
     */
    correctAmount(amount) {
      if (parseInt(amount, 10) === 0) return true;
      switch (this.selectedCurrency) {
        case 'bif':
        case 'clp':
        case 'djf':
        case 'gnf':
        case 'jpy':
        case 'kmf':
        case 'krw':
        case 'mga':
        case 'pyg':
        case 'rwf':
        case 'ugx':
        case 'vnd':
        case 'vuv':
        case 'xaf':
        case 'xof':
        case 'xpf':
          return parseInt(amount, 10) >= 100;
        case 'usd':
        case 'eur':
        case 'gbp':
          return parseInt(amount, 10) >= 1;
        default:
          return parseInt(amount, 10) >= 10;
      }
    },
  },
  data() {
    return {
      enabled: false,
      selectedCurrency: '',
      amounts: {
        first: 0,
        second: 0,
        third: 0,
      },
      translations: {
        button: {
          sv: '',
          en: '',
        },
        message: {
          sv: '',
          en: '',
        },
        thanks: {
          sv: '',
          en: '',
        },
      },
      donationTextLanguage: 'en',
      done: false,
      states: {
        amounts: '',
        texts: '',
      },
    };
  },
  async mounted() {
    await this.$store.dispatch('AccountManager/getCountryCurrencies');
    await this.getDonateSettings();

    this.$nextTick(() => {
      this.done = true;
    });
  },
};
</script>
